 

/**
 * Strip server-generated fields (id, version, monitors) from a rule so it
 * can be added to a different database via the Geotab "Add" API.
 */
export function prepareRuleForImport(rule: Record<string, any>): Record<string, any> {
  const { id: _id, version: _v, monitors: _m, ...rest } = rule;
  return {
    ...rest,
    groups: [{ id: "GroupCompanyId" }],
    condition: rest.condition ? stripConditionIds(rest.condition) : undefined,
  };
}

/** Recursively strip `id` fields from condition nodes. */
function stripConditionIds(cond: Record<string, any>): Record<string, any> {
  const { id: _id, ...rest } = cond;
  if (Array.isArray(rest.children)) {
    rest.children = rest.children.map(stripConditionIds);
  }
  return rest;
}

/** Info about a diagnostic reference found inside a rule. */
export interface DiagnosticRef {
  /** The diagnostic ID as stored in the rule. */
  id: string;
  /** Unit of measure hint (may be undefined). */
  unitOfMeasure?: string;
  /** JSON-path breadcrumb for display (e.g. "condition.children[0]"). */
  path: string;
}

/**
 * Extract all diagnostic references from a rule's condition tree.
 * Returns unique entries by diagnostic id.
 */
export function extractDiagnosticRefs(
  obj: Record<string, any>,
  path = "condition",
): DiagnosticRef[] {
  const refs: DiagnosticRef[] = [];

  if (obj.diagnostic?.id) {
    refs.push({
      id: obj.diagnostic.id,
      unitOfMeasure: obj.diagnostic.unitOfMeasure,
      path,
    });
  }

  if (Array.isArray(obj.children)) {
    obj.children.forEach((child: Record<string, any>, i: number) => {
      refs.push(...extractDiagnosticRefs(child, `${path}.children[${i}]`));
    });
  }

  return refs;
}

/** True when the id follows the Geotab KnownId pattern (e.g. DiagnosticXxxId). */
export function isKnownDiagnosticId(id: string): boolean {
  return /^Diagnostic[A-Z]/.test(id) && id.endsWith("Id");
}

/**
 * Replace a diagnostic ID everywhere it appears in a rule's condition tree
 * (including in `diagnostic.id` fields).  Returns a deep clone.
 */
export function replaceDiagnosticId(
  obj: Record<string, any>,
  oldId: string,
  newId: string,
): Record<string, any> {
  const clone: Record<string, any> = { ...obj };

  if (clone.diagnostic?.id === oldId) {
    clone.diagnostic = { ...clone.diagnostic, id: newId };
  }

  if (Array.isArray(clone.children)) {
    clone.children = clone.children.map((c: Record<string, any>) =>
      replaceDiagnosticId(c, oldId, newId),
    );
  }

  return clone;
}
